<template>
    <v-container 
        class="pt-0 pb-0"
        style="background-color: var(--v-listPageBackground-base);"
        fluid
    >
        <div ref="stickyHeader" :class="{ 'sticky-list-header': stickyHeader }">
<!-- HEADLINE -->
        <v-row style="background-color: var(--v-listPageHeadlineBackground-base);">
            <v-col 
                class="headline"
                :alvqa="alvqa + '-list-title'"
            >
                {{ $t(headline)}}
            </v-col>
        </v-row>
<!-- FILTERS -->
        <v-row 
            style="background-color: var(--v-listPageFilterBackground-base);"
        >
            <v-col :cols="searchCols" v-if="showFiltersRow">
                <SearchFilter 
                    @keydown.enter="filter" 
                    :context="context"
                    :alvqa="alvqa + '-list-textfield-search'"
                />
            </v-col>
            <slot name="filter"></slot>
            <span class="pt-6" v-if="showSearchIcon">
                <BaseIcon 
                    :disabled="loadingData"
                    :alvqa="alvqa + '-list-icon-search'"
                    @click="filter" 
                    icon="fa-search" 
                    :tooltipText="searchIconLangConst" 
                />
            </span>
            <slot name="period"></slot>
        </v-row>
        </div>
        <v-row>
            <v-col style="background-color: var(--v-listPageListBackground-base);">
                <slot name="dataTable"></slot>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import SearchFilter from '../_filters/SearchFilter'
import BaseIcon from '../_buttons/BaseIcon'

export default {
    name: "BaseListPage",
    components: {
        SearchFilter,
        BaseIcon
    },
    data() {
        return {
            loadingData: false
        }
    },
    props: {
        searchIconLangConst: {
            type: String,
            default: "lang_GENERAL_CAPTION_START_SEARCH"
        },
        headline: {
            type: String,
            required: true
        },
        context: String,
        searchCols: {
            type: String,
            default: "3"
        },
        showSearchIcon: {
            type: Boolean,
            default: true
        },
        showFiltersRow: {
            type: Boolean,
            default: true
        },
        stickyHeader: {
            type: Boolean,
            default: false
        },
        alvqa: {
            type: String,
            default: ''
        }
    },
    computed: {
        // Mapping of pages to their respective filter getters
        filterMap() {
            return {
                'Works': {
                    'search': 'getWorksSearch',
                    'quality': 'getSelectedWorksQualityFilter',
                    'status': 'getSelectedWorksStatusFilter'
                },
                'Sheets': {
                    'search': 'getSheetsSearch',
                    'type': 'getSelectedSheetsTypeFilter',  
                    'year': 'getSelectedSheetsYearsFilter'  
                },
                'PublishingProjects': {
                    'search': 'getPublishingProjectsSearch'
                },
                'Income': {
                    'search': 'getIncomeSearch',
                    'fromQuarter': 'getSelectedIncomeFromQuarter',  
                    'fromYear': 'getSelectedIncomeFromYear',  
                    'toQuarter': 'getSelectedIncomeToQuarter',  
                    'toYear': 'getSelectedIncomeToYear'  
                },
                'PublishingStatements': {
                    'search': 'getPublishingStatementsSearch',
                    'archiveFilter': 'getSelectedPublishingStatementsArchiveFilter',
                    'period': 'getSelectedPeriodYearPublishingStatements',
                    'time unit': 'getSelectedPeriodTimeUnitPublishingStatements',
                    'period number' : 'getSelectedPeriodNumberPublishingStatements',  
                },
                'PublishingStatistics': {
                    'search': 'getPublishingStatisticsSearch',  
                    'fromQuarter': 'getSelectedPublishingStatisticsFromQuarter', 
                    'fromYear': 'getSelectedPublishingStatisticsFromYear',
                    'toQuarter': 'getSelectedPublishingStatisticsToQuarter',
                    'toYear': 'getSelectedPublishingStatisticsToYear',
                    'typeOfRights': 'getSelectedPublishingStatisticsTypeOfRights'
                },
                'Tracks': {
                    'search': 'getTracksSearch',
                    'Labels': 'getSelectedTracksLabelsFilter',
                    'Quality': 'getSelectedTracksQualityFilter'  
                },
                'LabelReleases': {
                    'search': 'getLabelReleasesSearch',
                    'Labels': 'getSelectedLabelReleasesLabelsFilter',
                    'Quality': 'getSelectedLabelReleasesQualityFilter',  
                    'DigitalDistributionStatus': 'getSelectedLabelReleasesDigitalDistributionStatus', 
                    'Years': 'getSelectedLabelReleasesYearsFilter',
                    'CountryOfOrigin': 'getSelectedLabelReleasesCountryOfOriginFilter'
                },
                'LabelProjects': {
                    'search': 'getLabelProjectsSearch' 
                },
                'SalesData': {
                    'search': 'getSalesDataSearch', 
                },
                'LabelStatements': {
                    'search': 'getLabelStatementsSearch',
                    'archiveFilter': 'getSelectedLabelStatementsArchiveFilter',
                    'period': 'getSelectedPeriodYearLabelStatements',
                    'time unit': 'getSelectedPeriodTimeUnitLabelStatements',
                    'period number' : 'getSelectedPeriodNumberLabelStatements',
                },
                'Pricelists': {
                'search': 'getPricelistsSearch'
                },
                'Inventories': {
                    'search': 'getInventoriesSearch',
                    'Warehouses': 'getSelectedInventoriesWarehouses',
                    'ValueTypes': 'getSelectedInventoriesValueTypes',
                    'StockToDate': 'getInventoriesDetailsStockToDate'
                },
                'Shipping': {
                    'search': 'getShippingSearch',
                    'PeriodFromFilter': 'getSelectedShippingPeriodFromFilter',
                    'PeriodToFilter': 'getSelectedShippingPeriodToFilter'
                },
                'Invoices': {
                    'search': 'getInvoicesSearch',
                    'TypeFilter': 'getSelectedInvoicesTypeFilter',
                    'StatusFilter': 'getSelectedInvoicesStatusFilter',
                    'PeriodFromFilter': 'getSelectedInvoicesPeriodFromFilter',
                    'PeriodToFilter': 'getSelectedInvoicesPeriodToFilter',
                    'PeriodDateFilter': 'getSelectedInvoicesPeriodDateFilter'
                },
                'AzGroups': {
                    'search': 'getAzGroupsSearch',
                    'visibility': 'getSelectedAzGroupsVisibilityFilter'
                },
                'AzPersons': {
                    'search': 'getAzPersonsSearch',
                    'visibility': 'getSelectedAzPersonsVisibilityFilter'
                },
                'AzDistributors': {
                    'search': 'getAzDistributorsSearch',
                    'visibility': 'getSelectedAzDistributorsVisibilityFilter'
                },
                'AzSocieties': {
                    'search': 'getAzSocietiesSearch',
                    'visibility': 'getSelectedAzSocietiesVisibilityFilter'
                },
                'AzLive': {
                    'search': 'getAzLiveSearch'
                },
                'PublishingContracts': {
                    'search': 'getPublishingContractsSearch'
                },
                'LabelContracts': {
                    'search': 'getLabelContractsSearch'
                },
            };
        },
        // Dynamically fetch the filter values based on the current context
        currentFilters() {
            let filters = {};
            const filterGetters = this.filterMap[this.context] || {};
            for (const [filterName, getterName] of Object.entries(filterGetters)) {
                filters[filterName] = this.$store.getters[getterName];
            }
            return filters;
        }
    },
    
    methods: {
        // Publishes the fixed app-bar height and the pinned header height (headline + filters)
        // as CSS variables so the data-table can stack its action row and column headers
        // directly below the app-bar without overlapping the breadcrumb extension.
        updateStickyOffset() {
            const el = this.$refs.stickyHeader
            if (!el) return
            const bar = document.querySelector('.v-app-bar')
            const barHeight = bar ? Math.round(bar.getBoundingClientRect().height) : 128
            this.$el.style.setProperty('--alv-appbar-height', barHeight + 'px')
            this.$el.style.setProperty('--alv-sticky-header-height', el.offsetHeight + 'px')
        },
        filter() {
            this.loadingData = true;
            this.$store.commit('setCurrentPage', 1)

            if(this.$store._mutations['setSelected' + this.context]) this.$store.commit('setSelected' + this.context, [])
            if(this.$store._mutations['setIsAllSelected' + this.context]) this.$store.commit('setIsAllSelected' + this.context, false)
            if(this.$store._mutations['setExpanded' + this.context]) this.$store.commit('setExpanded' + this.context, [])
            if(this.$store._mutations['set' + this.context + 'Details']) this.$store.commit('set' + this.context + 'Details', {})
			
			this.$store.dispatch('fetch' + this.context).then(() => {
                this.loadingData = false;
            }).catch(() => {
                this.loadingData = false;
            });
            mixpanel.track('Searched in '+ this.context, {
                'Page': this.$route.path,
                ...this.currentFilters
            });
        }
    },
    mounted() {
        if (!this.stickyHeader) return
        this.$nextTick(this.updateStickyOffset)
        if (typeof ResizeObserver !== 'undefined' && this.$refs.stickyHeader) {
            this._stickyObserver = new ResizeObserver(() => this.updateStickyOffset())
            this._stickyObserver.observe(this.$refs.stickyHeader)
        }
    },
    beforeDestroy() {
        if (this._stickyObserver) this._stickyObserver.disconnect()
    }
}
</script>

<style scoped>
/* Pins headline + filters below the fixed app-bar (incl. breadcrumb extension) while the list scrolls */
.sticky-list-header {
    position: sticky;
    top: var(--alv-appbar-height, 128px);
    z-index: 4;
    background-color: var(--v-listPageBackground-base);
}
</style>